<template>
  <div v-resize="onResize" class="round-details mt-6 mb-12">
    <v-navigation-drawer
      v-model="dialog"
      class="shot-info-dialog"
      width="331"
      fixed
      right
      dark
      hide-overlay
      disable-resize-watcher
      disable-route-watcher
    >
      <v-list class="pb-0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6">Shot Information</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click.stop="closeDialog">
              <v-icon>close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-list v-if="dialog" class="pb-0">
        <Shotview :shot-i-d-prop="shotIDProp" single-player mobile-layout />
      </v-list>
      <v-list v-else style="height: 50vh"></v-list>
    </v-navigation-drawer>

    <v-layout v-if="loadingMatch && !polling" wrap>
      <v-flex xs12>
        <div v-if="loadingMatch && !polling" class="progress progress-main">
          <pulse-loader :color="$options.loaderColor" :size="$options.loaderSize"></pulse-loader>
        </div>
      </v-flex>
    </v-layout>

    <!-- /** This was originally fluid with app container width */ -->
    <v-container v-else ref="cardContainer" fluidx grid-list-md text-left>
      <transition name="fadeDown" appear>
        <CourseCardV3
          :details="roundDetails"
          :user="$route.params.user || user"
          :scorecard="scorecardV3"
          :score="score"
          :is-stroke-play="isStrokePlay"
          :is-scramble="isScramble"
          :game-mode="gameModeID"
          :show-scorecard="showScorecard"
        />
      </transition>

      <transition name="fadeDown" appear>
        <v-tabs
          v-model="active"
          background-color="primary"
          slider-color="#FFFFFF"
          dark
          class="round-details-tabs"
        >
          <v-tab v-for="item in tabItems" :key="item.value" ripple>
            {{ $t(`roundDetails.${item.value}`) }}
          </v-tab>

          <v-spacer></v-spacer>

          <v-flex xs4 sm6>
            <v-layout wrap justify-end fill-height class="mt-1">
              <v-tooltip top>
                <template #activator="{ on }">
                  <a slot="activator" v-on="on">
                    <download-csv
                      ref="download"
                      :data="roundShotData"
                      :name="dataFile"
                      class="pt-2 px-2 v-size--small theme--dark download-btn"
                    >
                      <v-icon class="theme--dark">file_download</v-icon>
                    </download-csv>
                  </a>
                </template>
                <span> Export Shot Data </span>
              </v-tooltip>

              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <ShareNetwork
                    network="email"
                    :url="meta.url"
                    :title="meta.title"
                    :description="meta.description"
                    :media="meta.image"
                  >
                    <v-btn v-bind="attrs" icon dark class="mt- mr-4" v-on="on">
                      <v-icon dark> mdi-email </v-icon>
                    </v-btn>
                  </ShareNetwork>
                </template>
                <span>Email Round Details</span>
              </v-tooltip>

              <v-flex xs6 sm2 md2>
                <p class="poll">{{ $t('roundDetails.polling') }}:</p>
              </v-flex>
              <v-flex xs6 sm2 md2>
                <v-switch
                  v-model="polling"
                  inset
                  :color="$vuetify.theme.dark ? 'white' : ''"
                  @click.native="togglePollingV3"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- SCORECARD ====================================== -->
          <v-tab-item v-for="item in tabItems" :key="item.id">
            <div
              v-if="item.value === 'scorecard' && showScorecard && scoreCardLoading"
              class="progress progress-main"
            >
              <pulse-loader
                :color="$options.loaderColor"
                :size="$options.loaderSize"
              ></pulse-loader>
            </div>

            <v-card
              v-else-if="
                item.value === 'scorecard' && showScorecard && !loadingShotData && !scoreCardLoading
              "
              flat
            >
              <transition-group name="fadeDown" appear>
                <div
                  v-if="!loadingShotData && scorecardV3"
                  :key="1"
                  class="hidden-xs-only scorecard-container"
                >
                  <!-- <ScoreCard
                    :userShots="scoreCardData"
                    :shotsLoading="userShotsLoading"
                    :gameMode="roundGameMode"
                    expanded
                  ></ScoreCard>
                  <v-divider class="my-4"></v-divider>
                  V3 Scorecard -->
                  <ScoreCardV3
                    :user-shots="scorecardV3"
                    :shots-loading="userShotsLoading"
                    :game-mode="roundGameMode"
                    expanded
                  ></ScoreCardV3>
                </div>
                <v-flex
                  :key="2"
                  xs12
                  hidden-sm-and-up
                  class="text-center pt-6 pb-2-0 scorecard-rotate"
                >
                  <v-icon>screen_rotation</v-icon>
                  <p>{{ $t('roundDetails.rotate') }}</p>
                </v-flex>
              </transition-group>
            </v-card>

            <v-card
              v-else-if="item.value === 'scorecard' && !loadingShotData && !showScorecard"
              flat
            >
              <v-card-text>
                <v-alert :value="true" outlined type="info" color="info">
                  {{ $t('roundDetails.scorecardNA') }}
                  <b>
                    <em>{{ roundDetails.gameMode }}</em>
                  </b>
                  {{ $t('roundDetails.rounds') }}
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- STATISTICS ====================================== -->
            <v-card v-if="item.value === 'statistics'" flat>
              <div v-if="loadingShotData" class="progress progress-main">
                <pulse-loader
                  :color="$options.loaderColor"
                  :size="$options.loaderSize"
                ></pulse-loader>
              </div>
              <transition v-else name="fadeDown" appear>
                <v-card-text class="px-0">
                  <v-row v-if="isGappingBagMapFitting" wrap justify="center" class="mb-4">
                    <v-col cols="12" sm="3" class="text-center">
                      <v-btn color="info" block @click="routeAnalytics">View APEX Analytics</v-btn>
                    </v-col>
                  </v-row>

                  <v-layout wrap>
                    <!-- DRIVING ACCURACY -->
                    <v-flex
                      v-if="!loadingShotData && !isGappingBagMapFitting"
                      xs12
                      sm6
                      md3
                      class="py-6"
                    >
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#5a95c8' }"
                        >
                          <!-- {{ drivingAccuracy.drivingAccuracyPercentage | utility_percentage }} -->
                          {{ drivingAccuracy.datasets[0].data[0] | utility_percentage }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chart-data="drivingAccuracy"
                            :chart-options="drivingAccuracyOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="drivingAccuracy"
                            :chartOptions="drivingAccuracyOptions"
                            chartType="doughnut"
                            chartID="222"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- GREENS IN REGULATION -->
                    <v-flex
                      v-if="!loadingShotData && !isGappingBagMapFitting"
                      xs12
                      sm6
                      md3
                      class="py-6"
                    >
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#78b058' }"
                        >
                          <!-- {{ greensInRegulation.girPercentage | utility_percentage }} -->
                          {{ greensInRegulation.datasets[0].data[0] | utility_percentage }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chart-data="greensInRegulation"
                            :chart-options="greensInRegulationOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="greensInRegulation"
                            :chartOptions="greensInRegulationOptions"
                            chartType="doughnut"
                            chartID="333"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- DRIVING DISTANCE -->
                    <v-flex
                      v-if="!loadingShotData && !isGappingBagMapFitting"
                      xs12
                      sm6
                      md3
                      class="py-6"
                    >
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#ba2127' }"
                        >
                          {{ nan(drivingDistance.datasets[0].data[0]) }}
                          {{ drivingDistance.datasets[0].data[0] ? ydsUnitType : '' }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chart-data="drivingDistance"
                            :chart-options="drivingDistanceOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="drivingDistance"
                            :chartOptions="drivingDistanceOptions"
                            chartType="doughnut"
                            chartID="444"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- PUTTING DISTANCE -->
                    <v-flex
                      v-if="!loadingShotData && !isGappingBagMapFitting"
                      xs12
                      sm6
                      md3
                      class="py-6"
                    >
                      <div class="rel">
                        <div
                          class="d-title"
                          :style="$vuetify.theme.dark ? { color: '#ffffff' } : { color: '#231f20' }"
                        >
                          {{ nan(puttingDistance.datasets[0].data[0]) }}
                          {{ puttingDistance.datasets[0].data[0] ? ftUnitType : '' }}
                        </div>
                        <div class="dg-chart">
                          <DoughnutChart
                            :chart-data="puttingDistance"
                            :chart-options="puttingDistanceOptions"
                            :width="300"
                            :height="230"
                          ></DoughnutChart>
                          <!-- <Chart
                            :chartData="puttingDistance"
                            :chartOptions="puttingDistanceOptions"
                            chartType="doughnut"
                            chartID="555"
                            :width="300"
                            :height="230"
                          ></Chart> -->
                        </div>
                      </div>
                    </v-flex>

                    <!-- AREA CHART/LINE CHART - Show both charts for stroke play -->
                    <v-flex v-if="isStrokePlay" xs12>
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData && hasParData"
                          class="area-chart"
                          :chart-data="roundParChartData"
                          :chart-options="areaParChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData && hasShotGraphData"
                          class="line-chart mt-6"
                          :chart-data="shotDistance"
                          :chart-options="lineChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                    </v-flex>

                    <!-- LINE CHART for non-stroke play rounds -->
                    <v-flex v-else-if="!isStrokePlay" xs12>
                      <transition name="fadeDown" appear>
                        <LineChart
                          v-if="!loadingShotData"
                          class="line-chart"
                          :chart-data="shotDistance"
                          :chart-options="lineChartOptions"
                          :height="!isMobile ? 100 : 300"
                        ></LineChart>
                      </transition>
                    </v-flex>

                    <!-- FILTER CLUB OPTION -->
                    <v-flex v-if="userShots && userShots.length" xs12 class="my-4">
                      <transition name="fadeDown" appear>
                        <v-layout justify-start fill-height wrap>
                          <v-flex xs3 sm2 md1 class="text-center text-sm-right mr-1">
                            <p class="filter-by">{{ $t('roundDetails.filterBy') }}:</p>
                          </v-flex>
                          <v-flex xs7 sm3 md2>
                            <v-select
                              v-model="filteredClubType"
                              :items="clubTypesV3"
                              class="filter-box"
                              label="Club Type"
                              single-line
                            ></v-select>
                          </v-flex>
                          <!-- <v-flex xs4 sm2 md1 class="text-center text-sm-right mr-1"> -->
                          <!-- <p class="units">{{ $t('roundDetails.filterBy') }}:</p> -->
                          <!-- <p class="units">Unit Measurement:</p> -->
                          <!-- </v-flex> -->

                          <!-- <v-flex xs7 sm3 md2>
                            <v-select
                              :items="unitTypeOptions"
                              class="units-box"
                              v-model="unitType"
                              label="Measurement"
                              single-line
                            ></v-select>
                          </v-flex> -->

                          <v-flex xs5 sm2 class="text-sm-right text-left mr-1">
                            <p class="filter-by">{{ $t('roundDetails.measurement') }}:</p>
                          </v-flex>
                          <v-flex xs6 sm3 md3>
                            <v-radio-group v-model="unitType" class="mt-4" row hide-details>
                              <v-radio
                                v-for="n in unitTypeOptions"
                                :key="n"
                                :label="`${n}`"
                                :value="n"
                              ></v-radio>
                            </v-radio-group>
                          </v-flex>
                        </v-layout>
                      </transition>

                      <!-- <div class="text-center pt-2">
                        <download-csv :data="json_data" class="primary btn download-btn">
                          Download Data
                        </download-csv>
                        <download-csv
                          :data="leaderboardData"
                          :name="dataFile"
                          :fields="fields"
                          :labels="labels"
                          v-on:export-finished="exported"
                          class="primary btn download-btn"
                        >
                          CSV
                        </download-csv>
                      </div> -->
                    </v-flex>

                    <!-- DATA TABLE OF SHOTS -->
                    <v-flex xs12 class="mt-0">
                      <transition name="fadeDown" appear>
                        <v-card flat class="data-table light-border">
                          <v-data-table
                            class="shot-data-table table-striped"
                            :headers="convertedHeaders"
                            :items="userShots"
                            :loading="loadingShotData"
                            :item-class="mulliganClass"
                            :footer-props="{
                              'items-per-page-options': [10, 15, { text: 'All', value: -1 }],
                              'items-per-page-text': 'Shots per page:',
                            }"
                            @click:row="shotClick"
                          >
                            <template #progress>
                              <v-progress-linear
                                slot:progress
                                color="info"
                                height="10"
                                indeterminate
                              ></v-progress-linear>
                            </template>

                            <template #[`item.courseHoleIndex`]="{ item }">
                              <!-- <span v-if="item.type === 'practice'">-</span> -->
                              <span v-if="practiceTypes.includes(item.type)">-</span>
                              <span v-else>
                                {{ item.hole ? item.hole : '-' }}
                              </span>
                            </template>

                            <template #[`item.shotIndex`]="{ item }">
                              <span :class="{ active: isActive === item.id }">
                                {{ item.shotIndex + 1 }}
                              </span>
                            </template>
                            <template #[`item.clubTypeKey`]="{ item }">
                              <span :class="{ active: isActive === item.id }">
                                {{ keyToFriendlyV3(item.detected_club_type) }}
                              </span>
                            </template>
                            <!-- Head Speed default fps -->
                            <template #[`item.clubHeadSpeed`]="{ item }">
                              {{ unitConversion(unitType, item.club_head_speed, 'fps', true) }}
                              <!-- unitConversion(unitType, Math.abs(roundNum(item.clubHeadSpeed * 0.681818))) -->
                              <!-- {{
                                Math.abs(roundNum(item.clubHeadSpeed * 0.681818))
                              }} -->
                            </template>
                            <!-- Distance - feet -->
                            <template #[`item.totalDistance`]="{ item }">
                              {{ unitConversion(unitType, item.totalDistance, 'yds', true) }}
                              <!-- {{ roundNum(item.totalDistance / 3) }} -->
                            </template>
                            <!-- Carry -->
                            <template #[`item.carryDistance`]="{ item }">
                              {{ nan(unitConversion(unitType, item.carryDistance, 'yds', true)) }}
                              <!-- {{ roundNum(item.carryDistance / 3) | nan }} -->
                            </template>
                            <!-- Ball Speed -->
                            <template #[`item.launchBallSpeed`]="{ item }">
                              {{ unitConversion(unitType, item.launchBallSpeed, 'fps', true) }}
                              <!-- {{ roundNum(item.launchBallSpeed * 0.681818) }} -->
                            </template>
                            <template #[`item.launchBackSpin`]="{ item }">
                              {{ roundNum(item.launch_back_spin) }}
                            </template>
                            <template #[`item.launchSideSpin`]="{ item }">
                              {{
                                item.launch_side_spin
                                  ? Math.abs(roundNum(item.launch_side_spin))
                                  : '-'
                              }}
                              <v-icon v-if="item.launch_side_spin < 0" small> arrow_back </v-icon>
                              <v-icon v-if="item.launch_side_spin > 0" small>
                                arrow_forward
                              </v-icon>
                            </template>
                            <template #[`item.launchDirection`]="{ item }">
                              {{
                                item.launch_direction
                                  ? Math.abs(roundNum(item.launch_direction)) + '&deg;'
                                  : '-'
                              }}
                              <v-icon v-if="item.launch_direction < 0" small> arrow_back </v-icon>
                              <v-icon v-if="item.launch_direction > 0" small>
                                arrow_forward
                              </v-icon>
                              <!-- {{ Math.abs(roundNum(item.launchDirection)) }}&deg; -->
                            </template>
                            <template #[`item.launchAngle`]="{ item }">
                              <!-- {{ roundNum(item.launchAngle) }}&deg; -->
                              {{ item.launch_angle ? roundNum(item.launch_angle) + '&deg;' : '-' }}
                            </template>
                          </v-data-table>
                        </v-card>
                      </transition>
                    </v-flex>

                    <!-- ROUND PARTICIPANTS -->
                    <v-flex v-if="roundPlayers && roundPlayers.length > 1 && !loadingShotData" xs12>
                      <transition name="fadeDown">
                        <v-card
                          v-if="roundPlayers && roundPlayers.length > 1 && !loadingShotData"
                          xs12
                          flat
                          class="players mt-2 mb-4"
                        >
                          <v-card-title primary-title>
                            <div class="text-h6">{{ $t('roundDetails.roundParticipants') }}</div>
                          </v-card-title>

                          <div v-if="loadingShotData" class="progress">
                            <pulse-loader
                              :color="$options.loaderColor"
                              :size="$options.loaderSize"
                            ></pulse-loader>
                          </div>

                          <v-data-table
                            :headers="playerHeaders"
                            :items="roundPlayers"
                            hide-default-footer
                            @click:row="clickedGolferRow"
                          >
                            <template #[`item.name`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.name || item.display_name }}
                              </span>
                            </template>
                            <template #[`item.id`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.player_id.substr(0, 8) }}
                              </span>
                            </template>
                            <template #[`item.teeLevel`]="{ item }">
                              <span class="text-left golfer fillTD">
                                {{ item.teeLevel || utility_capitalize(item.tee_level) }}
                              </span>
                            </template>
                          </v-data-table>
                        </v-card>
                      </transition>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </transition>
            </v-card>

            <!-- ANALYTICS ====================================== -->
            <!-- Letting everyone view analytics right now -->
            <!-- <v-card flat v-if="item.value === 'analytics' && active === 2 && isMe && hasAnalytics"> -->
            <v-card
              v-if="
                item.value === 'analytics' &&
                ((canViewAnalytics && active === 1) || active === 2) &&
                isMe
              "
              flat
              class="analytics-card"
            >
              <div v-if="loadingAA" class="progressAA">
                <pulse-loader
                  :color="$options.loaderColor"
                  :size="$options.loaderSize"
                ></pulse-loader>
              </div>
              <transition name="fadeDown" appear>
                <AdvancedAnalytics :round-i-d="roundID" :user-i-d="userID" />
              </transition>
            </v-card>

            <!-- Display this when we turn on permissions for analytics -->
            <!-- <v-card
            flat
            v-else-if="item.value === 'analytics' && active === 2 && isMe && !hasAnalytics"
          >
            <transition name="fadeDown" appear>
              <v-layout align-center justify-center row wrap fill-height class="py-12">
                <v-flex xs12 sm5 md5>
                  <v-card-title
                    class="display-1 font-weight-bold primary--text text-uppercase py-0"
                  >
                    Advanced Analytics
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <span class="title grey--text text--darken-2 text-uppercase">
                      Analyze your round
                    </span>
                  </v-card-text>
                  <v-card-text class="py-6">
                    Subscribe for access to in-depth analytics. With this tool you can sort and
                    compare every shot from two different rounds or see an aggregate table of all
                    shots selected. Visualize your data over 18 holes, with a top-down view of shots
                    selected.
                  </v-card-text>
                  <v-card-actions class="px-6">
                    <v-btn color="primary" large @click="subscribeAA">Subscribe</v-btn>
                  </v-card-actions>
                </v-flex>
                <v-flex xs12 sm6 md5>
                  <v-img :src="`${AASample}`" alt="Advanced Analytics" />
                </v-flex>
              </v-layout>
            </transition>
          </v-card> -->

            <v-card v-else-if="item.value === 'analytics' && active === 2 && !isMe" flat>
              <transition name="fadeDown" appear>
                <v-alert outlined type="info" color="info">
                  {{ $t('roundDetails.advAnalyticsVisibile') }}
                </v-alert>
              </transition>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </transition>

      <v-btn fixed bottom left fab dark small color="primary" @click="back">
        <v-icon dark>arrow_back</v-icon>
      </v-btn>

      <!-- <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <ShareNetwork
            network="email"
            :url="meta.url"
            :title="meta.title"
            :description="meta.description"
            :media="meta.image"
          >
            <v-btn
              v-bind="attrs"
              v-on="on"
              style="left: 65px"
              fixed
              bottom
              fab
              dark
              small
              color="success"
            >
              <v-icon dark> mdi-email </v-icon>
            </v-btn>
          </ShareNetwork>
        </template>
        <span>Email Round Details</span>
      </v-tooltip> -->
    </v-container>

    <v-dialog v-model="v3MatchRedirect" width="500">
      <v-card class="mx-auto disable_grey" dark color="info" max-width="500">
        <v-card-title>
          <v-icon large left> mdi-link </v-icon>
          <span class="text-h6 font-weight-light">Redirecting...</span>
        </v-card-title>

        <v-card-text class="text-h5 font-weight-bold">
          We're switching you to the updated match details view. Please wait.
          <div class="progress py-6">
            <pulse-loader color="#ffffff" size="0.75em"></pulse-loader>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// TODO REFACTOR OUT INTO SEPARATE COMPONENTS !!!!!!!!!!!
import { mapGetters } from 'vuex';
import meMixin from '@/mixins/meMixin';
import utilityMixin from '@/mixins/utilityMixin';
// import ScoreCard from '@/components/ScoreCard';
import ScoreCardV3 from '@/components/ScoreCardV3';
import CourseCardV3 from '@/components/CourseCardV3';
import AdvancedAnalytics from '@/components/AdvancedAnalytics';
import Shotview from '@/components/Shotview';
import DoughnutChart from '@/components/charts/DoughnutChart';
import LineChart from '@/components/charts/LineChart';
// import Chart from '@/components/charts/newCharts/Chart';
import AASample from '@/assets/AA-sample@2x.png';
import scorecardDataConfig from '../lib/scorecardDataConfig';
import browserDetector from '../lib/browserDetector';
import { loaderColor, loaderSize, scoreCardModes } from '@/lib/constants';
import { unitConversion } from '@/utils/helpers';
import {
  baseDoughnutOptions,
  baseDoughnutBGColor,
  baseChartOptions,
  baseLineChartOptions,
} from '@/lib/chartData.js';
import { buildPortalUrl } from '@/utils/portalNavigation';

const cmToFeetUnit = 30.48;

export default {
  name: 'RoundDetails',
  components: {
    // ScoreCard,
    ScoreCardV3,
    CourseCardV3,
    AdvancedAnalytics,
    Shotview,
    DoughnutChart,
    LineChart,
    // Chart,
  },

  // filters: {
  //   nan(val) {
  //     if (isNaN(val)) return '-';
  //     return val;
  //   },
  // },

  mixins: [meMixin, utilityMixin],

  metaInfo() {
    const url = window?.location?.href;
    const { displayName = '' } = this.me;
    const title = `Round Details - ${this.matchDetails?.game_settings?.course?.name}`;
    const imgOptions = `?w=1200&h=628&fit=crop&auto=compress,format&txt=${encodeURIComponent(
      title
    )}&txt-color=f8f9ff&txt-font=Helvetica%20Neue&txt-size=36&txt-align=right,bottom&txt-shad=5&txt-pad=20`;
    const formattedImg = `${this.roundDetails?.course?.imageURL}${imgOptions}`;

    this.meta.title = title;
    this.meta.url = url;
    this.meta.image = formattedImg;
    this.meta.description = `E6 Golf - ${displayName} round details and shot statistics.`;

    return {
      title: `${displayName} Round Details`,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        //* Facebook
        {
          name: 'og:type',
          content: 'website',
        },
        {
          vmid: 'og:site_name',
          name: 'og:site_name',
          content: 'E6 Golf Portal',
        },
        {
          vmid: 'og:title',
          name: 'og:title',
          content: `${displayName} Round Details`,
        },
        {
          vmid: 'og:description',
          name: 'og:description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        {
          vmid: 'og:url',
          name: 'og:url',
          content: url,
        },
        {
          vmid: 'og:image',
          name: 'og:image',
          content: formattedImg,
        },
        //* Twitter
        {
          vmid: 'twitter:title',
          name: 'twitter:title',
          content: `${displayName} Round Details`,
        },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: `E6 Golf - ${displayName} round details and shot statistics.`,
        },
        {
          vmid: 'twitter:url',
          name: 'twitter:url',
          content: url,
        },
        {
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: formattedImg,
        },
      ],
    };
  },

  data() {
    return {
      meta: {
        title: '',
        image: '',
        url: '',
        description: '',
      },

      dataFile: 'shot_data_export.csv',

      AASample,
      isActive: '',
      dialog: false,
      shotIDProp: null,
      score: null,
      analyticsVisible: false,
      active: null,
      loadingAA: false,
      calcLeft: '40vw',
      shotData: [],
      shotDataV3: [],
      loadingShotData: false,
      tabItems: [
        { value: 'scorecard', tab: 'Scorecard' },
        { value: 'statistics', tab: 'Statistics' },
        { value: 'analytics', tab: 'Advanced Analytics' },
      ],
      isMobile: false,
      search: '',
      pollingHandler: null,
      filteredClubType: 'All',
      unitType: `${this.$t('roundDetails.imperial')}`,
      // unitTypeOptions: ['Imperial', 'Metric'],
      // unitTypeOptions: [`${this.$t('roundDetails.imperial')}`, `${this.$t('roundDetails.metric')}`],
      polling: false,
      userShotsLoading: false,
      scoreCardData: {},
      scoreCardLoading: false,
      roundGameMode: '',
      gameModeID: '',
      pars: [],
      playerHeaders: [
        { text: this.$t('roundDetails.name'), align: 'left', value: 'name' },
        { text: this.$t('roundDetails.playerID'), align: 'left', value: 'id' },
        { text: this.$t('roundDetails.teeLevel'), align: 'left', value: 'teeLevel' },
      ],
      headers: [
        { text: 'Hole', align: 'left', value: 'courseHoleIndex' },
        { text: 'Shot', align: 'left', value: 'shotIndex' },
        { text: 'Club Type', value: 'clubTypeKey' },
        { text: 'Head Speed (mph)', value: 'clubHeadSpeed' },
        { text: 'Distance (yds)', value: 'totalDistance' },
        { text: 'Carry (yds)', value: 'carryDistance' },
        { text: 'Ball Speed (mph)', value: 'launchBallSpeed' },
        { text: 'Back Spin (rpm)', value: 'launchBackSpin' },
        { text: 'Side Spin (rpm)', value: 'launchSideSpin' },
        { text: 'Launch Direction', value: 'launchDirection' },
        { text: 'Launch Angle', value: 'launchAngle' },
      ],
      clubTypes: ['All'],
      clubTypesV3: ['All'],
      practiceTypes: ['practice', 'bag_mapping', 'club_fitting', 'gapping'],

      isV3Match: false,
      hasParData: false,
      v3MatchRedirect: false,
    };
  },

  computed: {
    ...mapGetters({
      user: 'users/user',
      stats: 'users/stats',
      hasAnalytics: 'users/hasAnalytics',

      matchDetails: 'rounds/matchDetails',
      loadingMatch: 'rounds/loadingMatch',
    }),

    unitTypeOptions() {
      return [`${this.$t('roundDetails.imperial')}`, `${this.$t('roundDetails.metric')}`];
    },

    matchGameMode() {
      if (this.matchDetails?.game_settings) {
        const {
          game_settings: { game_mode: gameMode },
        } = this.matchDetails;
        return gameMode;
      }
      return '';
    },

    isStrokePlay() {
      if (this.matchDetails) {
        return this.gameModeID?.toLowerCase() === 'stroke_play';
      }
      return false;
    },

    isScramble() {
      if (this.matchDetails) {
        return this.gameModeID?.toLowerCase() === 'scramble';
      }
      return false;
    },

    isBestBall() {
      if (this.matchDetails) {
        return this.gameModeID?.toLowerCase() === 'best_ball';
      }
      return false;
    },

    isStableford() {
      if (this.matchDetails) {
        return this.gameModeID?.toLowerCase() === 'stableford';
      }
      return false;
    },

    isGappingBagMapFitting() {
      if (this.matchDetails) {
        return (
          this.matchGameMode?.id?.toLowerCase() === 'gapping' ||
          this.matchGameMode?.id?.toLowerCase() === 'bag_mapping' ||
          this.matchGameMode?.id?.toLowerCase() === 'club_fitting'
        );
      }
      return false;
    },

    showScorecard() {
      if (this.gameModeID) {
        return this.isStrokePlay || this.isScramble || this.isBestBall || this.isStableford;
      }
      return false;
    },

    currentPlayer() {
      // TODO check for apex event eligibility in outer players array
      if (this.matchDetails?.report?.players) {
        const {
          report: { players = [] },
        } = this.matchDetails;
        const currentPlayer = players?.find((player) => {
          return player.id === this.$route.params.userID;
        });
        return currentPlayer ?? {};
      }

      return {};
    },

    allPlayers() {
      if (this.matchDetails?.report?.players) {
        const {
          report: { players = [] },
        } = this.matchDetails;
        return players ?? [];
      }
      if (this.matchDetails?.players) {
        const { players = [] } = this.matchDetails;
        return players ?? [];
      }
      return [];
    },

    currentTeams() {
      if (this.matchDetails?.report?.teams) {
        const {
          report: { teams = [] },
        } = this.matchDetails;
        return teams ?? [];
      }
      return [];
    },

    currentPlayerTeam() {
      const { id: userID } = this.currentPlayer;
      const team = this.currentTeams.find((item) => {
        let found;
        if (!item?.players) return {};
        for (const [key, value] of Object.entries(item.players)) {
          if (userID === key) {
            found = item;
          }
        }
        return found;
      });
      return team;
    },

    roundPlayers() {
      if (this.matchDetails?.players) {
        return this.matchDetails.players;
      }
      if (this.matchDetails?.report?.players) {
        return this.matchDetails?.report?.players;
      }
      return [];
    },

    convertedHeaders() {
      // if (this.unitType === 'Metric') {
      if (this.unitType === this.$t('roundDetails.metric')) {
        this.headers.map((item) => {
          if (item.text.includes('(mph)')) {
            item.text = item.text.replace('mph', 'kph');
          }
          if (item.text.includes('(yds)')) {
            item.text = item.text.replace('(yds)', '(m)');
          }
          return item;
        });
      }
      // if (this.unitType === 'Imperial') {
      if (this.unitType === this.$t('roundDetails.imperial')) {
        this.headers.map((item) => {
          if (item.text.includes('(kph)')) {
            item.text = item.text.replace('kph', 'mph');
          }
          if (item.text.includes('(m)')) {
            item.text = item.text.replace('(m)', '(yds)');
          }
          return item;
        });
      }
      return this.headers;
    },

    isMe() {
      return this.me?.id === this.$route?.params?.userID;
    },

    roundID() {
      if (this.$route?.params?.id) {
        return this.$route.params.id;
      }
      return null;
    },

    userID() {
      if (this.$route?.params?.userID) {
        return this.$route.params.userID;
      }
      return null;
    },

    hasShotGraphData() {
      return this.shotDistance?.datasets?.[0]?.data?.length > 0;
    },

    ftUnitType() {
      // @ unitType = Imperial return ft
      // @ unitType = Metric return m
      // @ default return ft
      return this.unitType === this.$t('roundDetails.imperial')
        ? 'ft'
        : this.unitType === this.$t('roundDetails.metric')
        ? 'm'
        : 'ft';
    },

    ydsUnitType() {
      // @ unitType = Imperial return yds
      // @ unitType = Metric return m
      // @ default return yds
      return this.unitType === this.$t('roundDetails.imperial')
        ? 'yds'
        : this.unitType === this.$t('roundDetails.metric')
        ? 'm'
        : 'yds';
    },

    //* ============================= Begin chart setup ============================
    doughnutBGColor() {
      return baseDoughnutBGColor(this.$vuetify.theme.dark);
    },

    doughnutOptions() {
      return baseDoughnutOptions(this.$vuetify.theme.dark);
    },

    //* Driving Accuracy Doughnut Chart Setup ==================
    drivingAccuracy() {
      const shotListV3 = this?.shotDataV3 ?? [];
      const teeShots = [];
      let fairwayShot = 0;
      let accuracy = 0;

      if (shotListV3.length) {
        shotListV3.forEach((shot) => {
          const {
            detcted_club_type: clubType,
            starting_position: startingPosition,
            mulligan, //! need mulligan value still
            score,
            user_id: userID,
          } = shot;

          //* Not sure why we're excluding score === 0
          // if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot.totalDistance || clubType?.toLowerCase() === 'putter' || mulligan) return;

          if (userID === this.$route.params.userID && startingPosition === 'tee') {
            teeShots.push(shot);
            const finalShot = shot?.snapshots?.find((data) => {
              return data.type === 'total' || data.type === 'final';
            });
            //* Calculate fairway shot
            this.checkTerrainV3(finalShot, 'kTerrainFairway') ? (fairwayShot += 1) : null;
          }
        });
        accuracy = teeShots ? fairwayShot / teeShots.length : 0;
      }

      return {
        labels: [this.$t('roundDetails.round'), this.$t('roundDetails.overall')],
        datasets: [
          {
            data: [
              !isNaN(accuracy) ? accuracy : 0,
              this.stats === undefined ? 0 : this.stats?.fairway_accuracy?.toFixed(2),
            ],
            backgroundColor: ['#5a95c8', this.doughnutBGColor],
          },
        ],
        drivingAccuracyPercentage: this.stats === undefined ? 0 : this.stats?.fairway_accuracy,
      };
      //* Below returns the overall accuracy stats instead of the round stats if that is wanted
      // return {
      //   labels: [`Valid Drives`, `Total Drives`],
      //   datasets: [
      //     {
      //       data: [
      //         Math.round(this.stats === undefined ? 0 : this.stats?.DrivingAccuracyValid),
      //         Math.round(this.stats === undefined ? 0 : this.stats?.DrivingAccuracyTotal),
      //       ],
      //       backgroundColor: ['#5a95c8', 'rgba(0, 0, 0, 0.2)'],
      //     },
      //   ],
      //   drivingAccuracyPercentage: this.stats === undefined ? 0 : this.stats?.DrivingAccuracy,
      // };
    },

    drivingAccuracyOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: this.$t('roundDetails.driveAccuracy'),
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* GIR Doughnut Chart Setup ==================
    greensInRegulation() {
      let gir = [];
      let holeCount = [];

      if (this.scorecardV3?.greensInRegulation) {
        holeCount = this.scorecardV3?.holeScores?.filter((data) => {
          return data !== null;
        });
        gir = this.scorecardV3?.greensInRegulation?.filter((data) => {
          return data === true;
        });
      }
      const girCalc = gir && holeCount ? (gir?.length / holeCount?.length).toFixed(2) : 0;

      const chartData = {
        labels: [this.$t('roundDetails.round'), this.$t('roundDetails.overall')],
        datasets: [
          {
            data: [
              !isNaN(girCalc) ? girCalc : 0,
              this.stats === undefined ? 0 : this.stats?.gir_percentage?.toFixed(2),
            ],
            backgroundColor: ['#78b058', this.doughnutBGColor],
          },
        ],
      };
      //* Below returns the overall GIR stats instead of the round stats if that is wanted
      // return {
      //   labels: [`Valid Shots`, `Overall`],
      //   datasets: [
      //     {
      //       data: [
      //         Math.round(this.stats === undefined ? 0 : this.stats?.GreenInRegulationValid),
      //         this.stats === undefined ? 0 : this.stats?.GreenInRegulation?.toFixed(2),
      //       ],
      //       backgroundColor: ['#78b058', this.doughnutBGColor],
      //     },
      //   ],
      //   girPercentage: this.stats === undefined ? 0 : this.stats?.GreenInRegulation,
      // };

      //* Check if data is all 0s; if it is, add dummy data to end with empty label
      chartData.datasets.forEach((dataset) => {
        if (
          dataset.data.every((el) => {
            return el === 0 || el === '0.00';
          })
        ) {
          dataset.data = [0, 0.0001];
        }
      });

      return chartData;
    },

    greensInRegulationOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: this.$t('roundDetails.gir'),
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Driving Distance Doughnut Chart Setup ==================
    drivingDistance() {
      const shotListV3 = this?.shotDataV3 ?? [];
      const teeShots = [];
      let roundBest = 0;

      if (shotListV3.length) {
        shotListV3.forEach((shot) => {
          const {
            detcted_club_type: clubType,
            starting_position: startingPosition,
            mulligan, //! need mulligan value still
            score,
            user_id: userID,
          } = shot;

          //* Not sure why we're excluding score === 0
          // if (!shot.totalDistance || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot.totalDistance || clubType?.toLowerCase() === 'putter' || mulligan) return;

          if (userID === this.$route.params.userID && startingPosition === 'tee') {
            teeShots.push(shot);
          }
          roundBest = teeShots.reduce((prev, curr) => {
            return curr.totalDistance > prev ? curr.totalDistance : prev;
          }, 0);
        });
      }

      const convertedDrivingDistAvgToCM = this.stats?.driver_average * cmToFeetUnit;

      // TODO Is there a way to spell out average and not wrap?
      const chartData = {
        labels: [
          this.$t('roundDetails.round'),
          `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.avg')}`,
        ],
        datasets: [
          {
            //* value in feet - divide by 3 to get yards
            data: [
              Math.round(unitConversion(this.unitType, roundBest, 'yds', true)),
              Math.round(
                this.stats === undefined
                  ? 0
                  : this.unitConversion(this.unitType, convertedDrivingDistAvgToCM, 'yds', true)
              ),
            ],
            backgroundColor: ['#ba2127', this.doughnutBGColor],
          },
        ],
      };

      //* Check if data is all 0s; if it is, add dummy data to end with empty label
      chartData.datasets.forEach((dataset) => {
        if (
          dataset.data.every((el) => {
            return el === 0 || el === '0.00';
          })
        ) {
          dataset.data = [0, 0.0001];
        }
      });

      return chartData;
    },

    drivingDistanceOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: `${this.$t('roundDetails.driveDist')} (${
              this.unitType === this.$t('roundDetails.imperial') ? 'yds' : 'm'
            })`,
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Putting Distance Doughnut Chart Setup ==================
    puttingDistance() {
      const shotListV3 = this?.shotDataV3 ?? [];
      const putts = [];
      let roundBest = 0;

      if (shotListV3?.length) {
        shotListV3?.forEach((shot) => {
          const {
            detected_club_type: clubType,
            user_id: userID,
            totalDistance,
            endedInHole,
          } = shot;
          if (!totalDistance || clubType?.toLowerCase() === 'putter') {
            if (userID === this.$route?.params?.userID && endedInHole) {
              putts.push(shot);
            }
          }

          roundBest = putts.reduce((prev, curr) => {
            return curr.totalDistance > prev ? curr.totalDistance : prev;
          }, 0);
        });
      }

      const convertedPuttDistAvgToCM = this.stats?.putter_average * cmToFeetUnit;

      return {
        //* change to Avg
        labels: [
          this.$t('roundDetails.round'),
          // `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.average')}`,
          `${this.$t('roundDetails.overall')} ${this.$t('roundDetails.avg')}`,
        ],
        datasets: [
          {
            data: [
              roundBest ? unitConversion(this.unitType, roundBest, 'feet', true) : undefined,
              Math.round(
                this.stats === undefined
                  ? 0
                  : this.unitConversion(this.unitType, convertedPuttDistAvgToCM, 'feet', true)
              ),
            ],
            backgroundColor: ['#231f20', this.doughnutBGColor],
          },
        ],
      };
    },

    puttingDistanceOptions() {
      return {
        ...this.doughnutOptions,
        plugins: {
          datalabels: {
            display: false,
          },
          title: {
            text: `${this.$t('roundDetails.puttDist')} (${
              this.unitType === this.$t('roundDetails.imperial') ? 'ft' : 'm'
            })`,
            ...this.doughnutOptions.plugins.title,
          },
        },
      };
    },

    //* Shot Line Chart Setup ==================
    shotDistance() {
      let userShots = this?.userShots ?? [];
      if (this.filteredClubType !== 'All') {
        userShots = this.userShots?.filter((shot) => {
          return this.keyToFriendlyV3(shot.detected_club_type) === this.filteredClubType;
        });
      }

      //* Removing  && shot.score !== 0 as practice rounds are setting score to 0
      const filter = (shot) => {
        return shot.detected_club_type?.toLowerCase() !== 'putter' && !shot.mulligan;
      };

      const distanceData = userShots?.filter(filter, []).map((shot) => {
        return Math.round(shot.totalDistance / cmToFeetUnit / 3);
      }, []);

      const carryData = userShots?.filter(filter, []).map((shot) => {
        return Math.round(shot.carryDistance / cmToFeetUnit / 3);
      }, []);

      const labels = userShots?.filter(filter, []).map((shot) => {
        return shot.shotIndex + 1;
      }, []);

      return {
        labels,
        datasets: [
          {
            label: this.$t('roundDetails.distance'),
            borderColor: '#769CD8',
            backgroundColor: '#769CD8',
            pointRadius: 5,
            fill: false,
            deviationStrokeColor: '#000',
            data: distanceData,
            // steppedLine: 'before',
            lineTension: 0,
          },
          {
            label: this.$t('roundDetails.carry'),
            borderColor: '#7AD876',
            backgroundColor: '#7AD876',
            pointRadius: 5,
            fill: false,
            data: carryData,
            // steppedLine: 'before',
            lineTension: 0,
          },
        ],
      };
    },

    lineChartOptions() {
      let fontColor = '#424242';
      let delayed;
      if (this.$vuetify.theme.dark) {
        fontColor = '#ffffff';
      }
      const options = baseLineChartOptions(this.$vuetify.theme.dark);

      // const optionsYdsUnitType = this.unitType === 'Imperial' ? 'yds' : 'm';
      const optionsYdsUnitType = this.unitType === this.$t('roundDetails.imperial') ? 'yds' : 'm';
      const optionsYdsUnitConversion = (val, type) => {
        if (!val) return '-';
        switch (this.unitType) {
          case 'Imperial':
            if (type === 'yds') {
              return val?.toFixed(0);
            }
            return val;
          case 'Metric':
            if (type === 'yds') {
              //* yards to meters divide by 1.094
              // console.log('(val / 1.094).toFixed(0):', (val / 1.094).toFixed(0));
              return (val / 1.094).toFixed(0);
            }
            return val;
          default:
            return Math.abs(this.roundNum(val * 0.681818));
        }
      };

      //* Override animation delay time
      options.animation = {
        ...options.animation,
        delay: (context) => {
          let delay = 0;
          if (context.type === 'data' && context.mode === 'default' && !delayed) {
            delay = context.dataIndex * 50 + context.datasetIndex * 100;
          }
          return delay;
        },
      };

      //* Override Y scale tick option
      options.scales.y = {
        ticks: {
          color: fontColor,
        },
      };

      //* Override gap option to allow
      options.spanGaps = false;

      //* Override tooltip data
      options.plugins.tooltip.callbacks = {
        label(tooltipItem) {
          const { label = '' } = tooltipItem?.dataset;
          const { formattedValue } = tooltipItem;
          const yards = formattedValue * 1.0 || 0.0;
          return `${label}: ${optionsYdsUnitConversion(
            Number(yards?.toFixed(0)),
            'yds'
          )} ${optionsYdsUnitType}`;
          // return `${label}: ${yards.toFixed(0)}${optionsYdsUnitType}`;
        },
      };

      return options;
    },

    //* Par Area Chart Setup ==================
    roundParChartData() {
      let pars = [];

      // console.log('this.scorecardV3:', this.scorecardV3);
      if (this.scorecardV3?.holeRoundScores) {
        for (let i = 0; i < this.scorecardV3?.holeRoundScores.length; i++) {
          if (
            this.scorecardV3?.holeRoundScores[i] !== undefined &&
            this.scorecardV3?.holeRoundScores[i] !== null
          ) {
            pars = [...pars, this.scorecardV3?.holeRoundScores?.[i]];
          }
        }
      }
      this.pars = pars;
      this.hasParData = this.pars?.length > 0;

      const labels = this.scorecardV3?.holeScores
        ?.map((shot, index) => {
          return shot !== null ? index + 1 : null;
        })
        .filter((data) => {
          return data !== null;
        });

      return {
        labels,
        datasets: [
          {
            label: 'Hole Par',
            borderColor: '#ba2127',
            backgroundColor: '#bcbec0',
            pointRadius: 5,
            fill: {
              target: 'origin',
              above: 'rgb(188,190,192)',
              below: 'rgb(120,176,88)',
            },
            deviationStrokeColor: '#000',
            data: this.pars,
            lineTension: 0,
          },
        ],
      };
    },

    areaParChartOptions() {
      const highestPar = Math.max.apply(null, this.pars);
      const lowestPar = Math.min.apply(null, this.pars);
      let maxPar = 4;
      let minPar = -3;
      const parAdjustment = 1;
      let fontColor = '#424242';
      if (this.$vuetify.theme.dark) {
        fontColor = '#ffffff';
      }

      maxPar = highestPar > 4 ? highestPar + parAdjustment : maxPar;
      minPar = lowestPar < -3 ? lowestPar - parAdjustment : minPar;

      const options = baseLineChartOptions(this.$vuetify.theme.dark, {
        isPar: true,
      });

      options.scales.y = {
        ticks: {
          color: fontColor,
          stepSize: 1,
          autoSkip: false,
        },
        min: minPar,
        max: maxPar,
      };
      return options;
    },
    //* ============================= End chart setup ============================

    //* ============================= User Shots Data Setup ============================
    userShots() {
      let shotListV3 = this?.shotDataV3 ?? [];
      const originalShotListV3 = shotListV3 || [];

      //* Filter by club.
      const clubTypesV3 = new Set(['All']);
      if (this.filteredClubType !== 'All') {
        shotListV3 = shotListV3?.filter((shot) => {
          return this.keyToFriendlyV3(shot.detected_club_type) === this.filteredClubType;
        });
      }

      shotListV3.forEach((shot) => {
        return clubTypesV3.add(this.keyToFriendlyV3(shot.detected_club_type));
      });
      originalShotListV3.forEach((shot) => {
        return clubTypesV3.add(this.keyToFriendlyV3(shot.detected_club_type));
      });

      this.clubTypesV3 = Array.from(clubTypesV3)?.sort((a, b) => {
        // Move 'All' to the beginning
        if (a === 'All') return -1;
        if (b === 'All') return 1;

        // Treat 'Driver' as '1 Wood'
        if (a === 'Driver') {
          a = '1 Wood';
        }
        if (b === 'Driver') {
          b = '1 Wood';
        }

        // Extract numeric values
        const aNumber = parseInt(a.match(/\d+/));
        const bNumber = parseInt(b.match(/\d+/));

        // Sort by numerical values
        if (!isNaN(aNumber) && !isNaN(bNumber)) {
          return aNumber - bNumber;
        }

        // Turn '1 Wood' back to 'Driver'
        if (a === '1 Wood') {
          a = 'Driver';
        }
        if (b === '1 Wood') {
          b = 'Driver';
        }

        // Sort remaining elements lexicographically
        return a.localeCompare(b);
      });

      const newShotList = shotListV3?.filter((shot) => {
        return shot.user_id === this.$route.params.userID;
      });
      return newShotList;
    },

    //* ============================= Round Data Setup ============================
    roundDetails() {
      // TODO pull in the tracking system with this or wait for it to be returned in round response
      if (!this.matchDetails?.game_settings) {
        return {};
      }

      const newData = {
        ...this.matchDetails?.game_settings,
      };
      // console.log('newData:', newData);
      // console.log('this.matchDetails:', this.matchDetails);

      return {
        title: `Round Details - ${newData?.course?.courseName ?? newData?.course?.name}`,
        createdAt: this.matchDetails?.created_at ?? '',
        gameMode: newData?.game_mode?.name,
        game: this.isV3Match ? 'apex' : 'connect',
        score_display: this.isV3Match ? this.currentPlayer?.score_display : null,
        course: newData?.course,
        mulliganSetting: this.utility_capitalize(newData?.mulligan_setting) ?? 'N/A',
        mulligans: newData?.mulligans < 0 ? 'Unlimited' : newData?.mulligans,
        teeLevel:
          this.currentPlayer === undefined
            ? ''
            : this.utility_capitalize(this.currentPlayer?.tee_level),
        fairwayHardness: newData?.fairway_hardness,
        eventID: this.matchDetails?.event?.id,
        eventEligible: this.currentPlayer?.eligible_for_event ?? false,
        event: this.matchDetails?.event,
        elevation: `${this.utility_roundNum(newData?.elevation) ?? 0} ft`,
        stimpRating: newData?.stimp_rating,
        greenHardness: this.utility_capitalize(newData?.green_hardness),
        wind: this.utility_capitalize(newData?.wind_setting),

        // pinPosition: newData?.pins_to_play ? newData?.pins_to_play?.[0] + 1 : 0, // TODO only pull first or show all
        pinPosition: newData?.pins_to_play
          ? newData?.pins_to_play
              .map((pin) => {
                return pin + 1;
              })
              .join(', ')
          : '-', // TODO only pull first or show all - compensate by 1

        puttingMode: this.computePuttingModev3(newData?.putting_mode, newData?.putting_range), //* revisit with putting_range value
        autoCaddie: newData?.auto_caddy,
        scoringPenalty: newData?.scoring_penalty,
        terrainPenalty: newData?.terrain_penalty,
        currentPlayer: this.currentPlayer,
        players: this.allPlayers?.length,
        team: this.currentPlayer?.team_index,
        holeCountSelected: newData?.holes_to_play?.length,
        selectedHoles: newData?.holes_to_play,
        roundHandicap: this.currentPlayer?.handicap?.player_handicap ?? null,
        qualifiedForHandicap: this.currentPlayer?.handicap?.eligible ?? false,
        // trackingSystem: '', // TODO show this
        data: [
          [
            { header: 'Format', value: newData?.game_mode?.name },
            {
              header: 'Tee Level',
              value:
                this.currentPlayer === undefined
                  ? ''
                  : this.utility_capitalize(this.currentPlayer?.tee_level),
            },
            {
              header: 'Mulligan Setting',
              value: newData?.mulligan_setting < 0 ? 'Unlimited' : newData?.mulligan_setting,
            },
            { header: 'Fairway Hardness', value: newData?.fairway_hardness },
            { header: 'Elevation', value: `${newData?.elevation ?? 0} ft` },
            { header: 'Stimp Rating', value: newData?.stimp_rating },
            { header: 'Green Hardness', value: newData?.green_hardness },
            { header: 'Wind', value: newData?.wind_setting },
            { header: 'Pin Position', value: newData?.pins_to_play ? newData?.pins_to_play : '-' },
            {
              header: 'Putting Mode',
              value: this.computePuttingModev3(newData?.putting_mode, newData?.putting_range),
            },
            // { header: 'Tracking System', value: trackingSystemKey },
            { header: 'AutoCaddie', value: newData?.auto_caddy?.toUpperCase() ?? 'N/A' },
            { header: 'Scoring Penalty', value: newData?.scoring_penalty },
          ],
        ],
      };
    },

    //* ============================= Scorecard Data Setup ============================
    //= ===============================================================================================================
    scorecardV3() {
      this.scoreCardLoading = true;

      if (!this.matchDetails?.report) {
        this.scoreCardLoading = false;
        // return {};
      }

      const { segments } = this.currentPlayer;

      let processedScores = {};
      let scrambleData = [];

      if (this.currentTeams?.length) {
        if (this.gameModeID?.toLowerCase() === 'scramble') {
          scrambleData = this.formatScrambleData(this.currentTeams);
          processedScores = this.processScoringData({
            segments,
            teamSegments: this.currentPlayerTeam?.segments,
            teamData: this.currentTeams,
            isScramble: true,
          });
        } else {
          processedScores = this.processScoringData({
            segments,
            teamSegments: this.currentPlayerTeam?.segments,
            isBestBall: true,
          });
        }
      } else {
        processedScores = this.processScoringData({ segments });
      }
      // console.log('processedScores:', processedScores);

      // TODO look at stableford
      const scoringData = {
        // holeNumber: this.dataLoop(segments, 'course_hole') || [],
        // holeHandicaps: this.dataLoop(segments, 'segment_handicap') || [],
        // holePars: this.dataLoop(segments, 'segment_par') || [],
        // holeScores: this.dataLoop(segments, 'score') || [],
        // holeRoundScores: this.dataLoop(segments, 'round_score') || [],
        // holeNetScores: this.dataLoop(segments, 'score_with_handicap') || [],
        // holeStableFordTotals: [],
        // stablefordScores: [],
        // bestBallTeamScores: [],
        // teeClubs: this.dataLoop(segments, 'club_type') || [],
        // fairwayShots: this.dataLoop(segments, 'tee_shot_on_fairway') || [],
        // sandShots: this.dataLoop(segments, 'sand_save') || [],
        // greensInRegulation: this.dataLoop(segments, 'gir') || [],
        // mulligans: this.dataLoop(segments, 'mulligans') || [],
        // penalties: this.dataLoop(segments, 'penalties') || [],
        // puttingShots: this.dataLoop(segments, 'putts') || [],
        ...processedScores,
        // strokeData,
        scrambleTeamScores: [...scrambleData],
        handicap: this.currentPlayer?.handicap?.player_handicap?.toString(),
      };
      // console.log('scoringData:', scoringData);
      this.scoreCardLoading = false;

      // console.log('scoringData:', scoringData);
      return scoringData ?? {};
    },

    // fields:[
    //   "id",
    //   "member",
    //   "handicap",
    //   "gender",
    //   "handed",
    //   "teebox",
    //   "events",
    //   "wins",
    //   "hours",
    //   "active",
    //   "joined",
    //   "userhandicap",
    //   "email_opt_in"
    // ]

    // labels: {
    //   "id": "Id",
    //   "member": "Member",
    //   "handicap": "Handicap",
    //   "gender": "Gender",
    //   "handed": "Handed",
    //   "teebox": "Teebox",
    //   "events": "Events",
    //   "wins": "Wins",
    //   "hours": "Hours",
    //   "active": "Active",
    //   "joined": "Joined",
    //   "userhandicap": "UserHandicap",
    //   "email_opt_in": "Email_opt_in"
    // }

    // convertedHeaders() {
    //   // if (this.unitType === 'Metric') {
    //   if (this.unitType === this.$t('roundDetails.metric')) {
    //     this.headers.map((item) => {
    //       if (item.text.includes('(mph)')) {
    //         item.text = item.text.replace('mph', 'kph');
    //       }
    //       if (item.text.includes('(yds)')) {
    //         item.text = item.text.replace('(yds)', '(m)');
    //       }
    //       return item;
    //     });
    //   }
    //   // if (this.unitType === 'Imperial') {
    //   if (this.unitType === this.$t('roundDetails.imperial')) {
    //     this.headers.map((item) => {
    //       if (item.text.includes('(kph)')) {
    //         item.text = item.text.replace('kph', 'mph');
    //       }
    //       if (item.text.includes('(m)')) {
    //         item.text = item.text.replace('(m)', '(yds)');
    //       }
    //       return item;
    //     });
    //   }
    //   return this.headers;
    // },

    roundShotData() {
      let data = [];
      data = this.userShots;
      // console.log(this.userShots);
      this.dataFile = `shot_data_export_${
        this.roundDetails.createdAt !== undefined ? this.roundDetails.createdAt : 'shot'
      }.csv`;
      // this.dataFile = `${
      //   this.$route?.params?.id !== undefined ? this.$route?.params?.id : 'shot'
      // }_shot_data_export.csv`;

      function deleteKey(obj, a, b) {
        obj[a] = obj[b];
        delete obj[b];
      }

      data = this.userShots.map((data) => {
        const formatData = {
          course: this.roundDetails?.course?.name,
          hole: data?.hole ?? data?.hole_name,
          // hole: data?.type === 'practice' ? '-' : data?.hole,
          shot: data?.shotIndex + 1,
          'club type': this.keyToFriendlyV3(data?.detected_club_type),
          // 'head speed': data?.club_head_speed,
          'head speed (mph)': unitConversion(this.unitType, data?.club_head_speed, 'fps', true),
          // 'total distance': data?.max_distance,
          'total distance (yds)': unitConversion(this.unitType, data?.totalDistance, 'yds', true),
          // 'carry distance': data?.carryDistance,
          'carry distance (yds)': unitConversion(this.unitType, data?.carryDistance, 'yds', true),
          'apex height (ft)': unitConversion(this.unitType, data?.apexData?.height, 'feet', true),
          // 'ball speed': data?.launchBallSpeed,
          'ball speed (mph)': unitConversion(this.unitType, data?.launchBallSpeed, 'fps', true),
          'back spin': data?.launch_back_spin,
          'side spin': data?.launch_side_spin,
          'launch dir': data?.launch_direction,
          'launch angle': data?.launch_angle,
        };

        for (const item in formatData) {
          if (item) {
            let keyVal = item;
            if (this.unitType === this.$t('roundDetails.metric')) {
              if (item.includes('(mph)')) {
                keyVal = item.replace('mph', 'kph');
                deleteKey(formatData, keyVal, item);
              }
              if (item.includes('(yds)')) {
                keyVal = item.replace('(yds)', '(m)');
                deleteKey(formatData, keyVal, item);
              }
              if (item.includes('(ft)')) {
                keyVal = item.replace('(ft)', '(m)');
                deleteKey(formatData, keyVal, item);
              }
            }

            if (this.unitType === this.$t('roundDetails.imperial')) {
              if (item.includes('(kph)')) {
                keyVal = item.replace('kph', 'mph');
                deleteKey(formatData, keyVal, item);
              }
              if (item.includes('(m)')) {
                keyVal = item.replace('(m)', '(yds)');
                deleteKey(formatData, keyVal, item);
              }
            }
          }
        }

        return formatData;
      });

      return data;
    },

    canViewAnalytics() {
      return this.tabItems.some((item) => {
        return item.value === 'analytics';
      });
    },

    // * Not used currently. Leaving in for future reference
    // averageTotalDistance() {
    //   return this.averageData('totalDistance', '#769CD8', (val) => {
    //     return val / 3.0;
    //   });
    // },

    // averageCarryDistance() {
    //   return this.averageData('carryDistance', '#7AD876', (val) => {
    //     return val / 3.0;
    //   });
    // },

    // averageBackSpin() {
    //   return this.averageData('launchBackSpin', '#F49F57');
    // },

    // averageBallSpeed() {
    //   return this.averageData('launchBallSpeed', '#76D8D8', (val) => {
    //     return val * 0.681818;
    //   });
    // },

    // TODO potentially use this to show/hide tabs. Causing display issues currently
    // filteredTabs() {
    //   let tabArr = [...this.tabItems];

    //   if (!this.isStrokePlay && !this.loadingMatch && !this.scoreCardLoading) {
    //     tabArr = this.tabItems.filter((tab) => {
    //       return tab.value !== 'scorecard';
    //     });
    //   }
    //   return tabArr;
    // },
  },

  watch: {
    polling(newVal, oldVal) {
      if (!newVal) {
        clearInterval(this.pollingHandler);
      }
    },

    //* Fake a loading indicator for AA as it's out of our state management
    active(newVal, oldVal) {
      if (newVal === 2 || (this.canViewAnalytics && newVal === 1)) {
        this.getWidth();
        this.loadingAA = true;
        setTimeout(() => {
          this.loadingAA = false;
        }, 2500);
      }
    },

    unitType(newVal) {
      //* set local storage
      localStorage.setItem('e6UserMeasurement', newVal);
    },

    isV3Match(newVal) {
      if (newVal) {
        this.v3MatchRedirect = true;
        this.handleNewPortalNavigation();
      }
    },
  },

  beforeCreate() {
    this.$store.dispatch('shots/resetState');
  },

  created() {
    console.clear();
    console.log('in v3 round details=========');
    //* non-reactive constants
    this.$options.loaderColor = loaderColor;
    this.$options.loaderSize = loaderSize;
    this.$options.scoreCardModes = scoreCardModes;
  },

  mounted() {
    this.scoreCardLoading = true;
    this.loadingShotData = true;
    this.unitType =
      localStorage.getItem('e6UserMeasurement') !== 'undefined' &&
      localStorage.getItem('e6UserMeasurement') !== null
        ? localStorage.getItem('e6UserMeasurement')
        : 'Imperial';
    //* set local storage
    localStorage.setItem('e6UserMeasurement', this.unitType);

    try {
      //* v3 match get
      this.$store
        .dispatch('rounds/matchGet', { id: this.$route.params.id })
        .then((res) => {
          console.log('matchGet res:', res);
          const {
            game_settings: {
              game_mode: { id: gameModeID },
            },
            shots,
          } = res;
          this.gameModeID = gameModeID; //* turn on later
          this.roundGameMode = gameModeID; //* turn on later

          const displayScoreCard = this.$options.scoreCardModes.includes(gameModeID);
          //* Set statistics tab to active if not stroke play round
          if (!displayScoreCard) {
            this.tabItems.shift();
            this.active = '0';
          }

          this.score = this.currentPlayer?.match_score ? this.currentPlayer?.match_score : null;
          this.shotDataV3 = this.formatShotData(shots);
          this.loadingShotData = false;
        })
        .catch((err) => {
          console.error('err:', err);
          this.loadingShotData = false;
          this.scoreCardLoading = false;
        });

      // TODO see if there's a check in the above endpoint to return if apex or not
      this.$store
        .dispatch('rounds/show', { id: this.$route.params.id })
        .then((_res) => {
          // console.log('connect res:', res);
          this.isV3Match = false;
        })
        .catch(() => {
          //* v3 rounds aren't analytics compatible just yet
          this.isV3Match = true;
          this.tabItems.pop();
          this.active = '0';
          this.loadingShotData = false;
          this.scoreCardLoading = false;
        });

      if (
        (this.$route?.params?.userID !== 'undefined' ||
          this.$route?.params?.userID !== undefined) &&
        this.user?.id !== this.$route?.params?.userID
      ) {
        this.$store.dispatch('users/show', this.$route?.params?.userID);
        this.$store.dispatch('users/stats', this.$route?.params?.userID);
      }
    } catch (error) {
      this.loadingShotData = false;
      this.scoreCardLoading = false;
      console.error('error:', error);
    }
  },

  beforeDestroy() {
    this.destroyPolling(this.pollingHandler);
    this.$store.dispatch('shots/resetState');
  },

  methods: {
    unitConversion,

    nan(val) {
      if (isNaN(val)) return '-';
      return val;
    },

    formatShotData(shotData) {
      // console.log('shotData==================:', shotData);
      if (!shotData) return [];
      const data = shotData
        ?.filter((shot) => {
          return shot.user_id === this.$route.params.userID;
        })
        ?.sort((a, b) => {
          return a.created_at > b.created_at ? 1 : -1;
        })
        ?.map((shot, i) => {
          const {
            snapshots,
            status,
            max_distance: maxDistance,
            final_resting_state: finalRestingState,
            launch_speed: launchSpeed,
          } = shot;
          return {
            ...shot,
            hole: shot?.hole_name?.includes('hole')
              ? shot?.hole_name?.match(/\d+/g)?.toString()
              : shot?.course_hole_num,
            shotIndex: i,
            mulligan: status === 'mulligan',
            endedInHole: finalRestingState === 'hole',
            totalDistance:
              maxDistance ||
              (snapshots?.find((snap) => {
                return snap?.type === 'total' || snap?.type === 'final';
              })?.distance ??
                null),
            carryDistance:
              snapshots?.find((snap) => {
                return snap?.type === 'carry';
              })?.distance ?? null,
            launchBallSpeed: launchSpeed ?? null,
            // launchBallSpeed:
            //   launchSpeed || launchSpeed !== 0
            //     ? launchSpeed
            //     : snapshots?.find((snap) => {
            //         return snap.type === 'launch';
            //       })?.speed ?? null,
            apexData: snapshots?.find((snap) => {
              return snap?.type === 'apex';
            }),
          };
        });
      // console.log('data:', data);
      return data;
    },

    formatScrambleData(data = []) {
      const formattedData = data?.map((item) => {
        return {
          team: item?.index?.toString(),
          color: item?.color,
          name: item?.name,
          scores: item?.segments?.map((s) => {
            return s?.score;
          }),
        };
      });
      return formattedData;
    },

    processScoringData({
      segments = [],
      teamSegments = [],
      teamData = [],
      isBestBall = false,
      isScramble = false,
    }) {
      const holeArray = new Array(18).fill(null);
      const { segment_labels = [] } = this.matchDetails?.report || {};
      // const { score_display } = this.currentPlayer;

      // segments.push(
      //   {
      //     segment_par: 5,
      //     course_hole: 8,
      //     segment_handicap: 1,
      //   },
      //   {
      //     segment_par: 4,
      //     course_hole: 9,
      //     segment_handicap: 8,
      //   }
      // );

      const scorecardStuff = {
        holeNumber: [],
        holeHandicaps: [],
        holePars: [],
        holeScores: [],
        holeRoundScores: [],
        holeNetScores: [],
        holeStableFordTotals: [],
        stablefordScores: [],
        bestBallTeamScores: [],
        scrambleTeamScores: [],
        teeClubs: [],
        fairwayShots: [],
        sandShots: [],
        greensInRegulation: [],
        mulligans: [],
        penalties: [],
        puttingShots: [],
      };

      for (const prop in scorecardStuff) {
        if (prop) {
          holeArray.forEach((hole, x) => {
            if (isBestBall) {
              //* use team segments
              teamSegments?.forEach((item, i) => {
                if (x === i) {
                  switch (prop) {
                    case 'holeNumber':
                      holeArray[x] = item?.course_hole;
                      break;
                    case 'holeHandicaps':
                      holeArray[x] = item?.segment_handicap;
                      break;
                    case 'holePars':
                      holeArray[x] = item?.segment_par;
                      break;
                    case 'bestBallTeamScores':
                      holeArray[x] = item.score;
                      break;

                    default:
                      holeArray[x] = null;
                      break;
                  }
                }
                return null;
              });
            } else {
              segments?.forEach((item, i) => {
                if (x === i) {
                  switch (prop) {
                    case 'holeNumber':
                      if (!item?.course_hole) {
                        holeArray[x] = item?.segment_number;
                        break;
                      }
                      if (!item?.segment_number && !item?.course_hole) {
                        holeArray[x] = Number(segment_labels[x]);
                        break;
                      }
                      holeArray[x] = item?.course_hole;
                      break;
                    case 'holeHandicaps':
                      holeArray[x] = item?.segment_handicap;
                      break;
                    case 'holePars':
                      holeArray[x] = item?.segment_par ?? null;
                      break;
                    case 'holeScores':
                      holeArray[x] = item?.score ?? null;
                      break;
                    case 'holeRoundScores':
                      // holeArray[x] = item?.round_score;
                      holeArray[x] =
                        item?.round_score ||
                        (isNaN(item?.score - item?.segment_par)
                          ? null
                          : item?.score - item?.segment_par);
                      break;
                    case 'holeNetScores':
                      holeArray[x] = item?.score_with_handicap ?? null;
                      break;
                    case 'holeStableFordTotals':
                      holeArray[x] = null;
                      break;
                    case 'stablefordScores':
                      holeArray[x] = null;
                      break;
                    case 'bestBallTeamScores':
                      holeArray[x] = null;
                      break;
                    case 'scrambleTeamScores':
                      holeArray[x] = null;
                      break;
                    case 'teeClubs':
                      holeArray[x] = this.formatClubType(item?.club_type);
                      break;
                    case 'fairwayShots':
                      holeArray[x] = item?.stroke_play_data?.tee_shot_on_fairway ?? null;
                      break;
                    case 'sandShots':
                      holeArray[x] = item?.stroke_play_data?.sand_save ?? null;
                      break;
                    case 'greensInRegulation':
                      holeArray[x] = item?.stroke_play_data?.gir ?? null;
                      break;
                    case 'mulligans':
                      holeArray[x] = item?.stroke_play_data?.mulligans ?? null;
                      break;
                    case 'penalties':
                      holeArray[x] = item?.stroke_play_data?.penalties ?? null;
                      break;
                    case 'puttingShots':
                      holeArray[x] = item?.stroke_play_data?.putts ?? null;
                      break;

                    default:
                      holeArray[x] = null;
                      break;
                  }
                }
                return null;
              });
            }
          });
          scorecardStuff[prop] = [...holeArray];
        }
      }
      // console.log('scorecardStuff:', scorecardStuff);
      return scorecardStuff;
    },

    shotClick(shot) {
      // console.log('shot:', shot);
      if (this.isV3Match) {
        this.dialog = false;
        return;
      }
      this.shotIDProp = shot.id;
      this.dialog = true;
      this.isActive = shot.id;

      //* Route to user round
      // this.$router.push({
      //   name: 'shotview',
      //   props: true,
      //   params: {
      //     shotID: shot.id,
      //   },
      // });
    },

    closeDialog() {
      this.dialog = false;
      this.isActive = '';
    },

    getWidth() {
      //* Calculate a mid width for faux loading indicator for AA
      this.calcLeft = `${this.$refs.cardContainer.clientWidth / 2}px`;
    },

    next() {
      const active = parseInt(this.active);
      this.active = active < 2 ? active + 1 : 0;
    },

    roundNum(num) {
      if (!num && num !== 0) return;
      return Number(num)?.toFixed(2);
    },

    keyToFriendlyV3(key) {
      if (key === undefined) return key;
      return key
        .replace(/(^|_)([a-z0-9])/gi, (_, prefix, letter) => {
          return (prefix === '_' ? ' ' : '') + letter?.toUpperCase();
        })
        .trim();
    },

    formatClubType(club) {
      if (!club) {
        return '-';
      }
      if (club === 'none') {
        return '...';
      }
      const parts = club?.split('_');
      const formatted = parts
        ?.map((part, index) => {
          if (!isNaN(part)) {
            // If the part is a number, return it as is
            return part;
          }
          if (parts.length === 1) {
            // If there's no underscore, return the first two letters: first uppercase, second lowercase
            return part[0].toUpperCase() + part[1].toLowerCase();
          }
          // Otherwise, take the first letter of each word part, first part in uppercase, subsequent in lowercase
          return index === 0 ? part[0].toUpperCase() : part[0].toLowerCase();
        })
        .join('');
      return formatted;
      // return club?.replace('_', '')?.toUpperCase()?.slice(0, 2);
    },

    computePuttingModev3(mode, range) {
      let modeFormat = '';
      switch (mode) {
        case 'gimme':
          modeFormat = 'Gimme';
          break;
        case 'playerPuttsOut':
        case 'player_putts_out':
          modeFormat = 'Player Putts Out';
          break;
        case 'computerDecides':
        case 'computer_decides':
          modeFormat = 'Computer Decides';
          break;
        case 'autoScore':
        case 'auto_scoring':
          modeFormat = 'Autoscore';
          break;
        case 'playerDecides':
        case 'player_decides':
          modeFormat = 'Player Decides';
          break;
        case 'none':
          modeFormat = 'None';
          break;
        default:
          return '-';
      }
      if (range) {
        modeFormat = `${(range / 30.48).toFixed(0)} Ft ${modeFormat}`;
      }
      return modeFormat;
    },

    //* V3 Polling ============================
    togglePollingV3() {
      if (this.pollingHandler === null && this.polling) {
        this.pollingHandler = setInterval(() => {
          const { status } = this.matchDetails;
          if (status === 'finished') {
            this.destroyPolling(this.pollingHandler);
            this.$root.vtoast.show({
              text: `Round finished. No more shots to record.`,
              color: 'info',
            });
          }

          try {
            //* v3 match get
            this.$store.dispatch('rounds/matchGet', { id: this.$route.params.id }).then((res) => {
              const { shots = [] } = res;

              //* v3 shot data
              this.shotDataV3 = this.formatShotData(shots);
              this.loadingShotData = false;
            });
          } catch (error) {
            this.loadingShotData = false;
            console.error('error:', error);
          }
        }, 7000);
      } else {
        this.destroyPolling(this.pollingHandler);
      }
    },

    destroyPolling(handler) {
      clearInterval(handler);
      this.pollingHandler = false;
      this.pollingHandler = null;
      this.polling = false;
    },

    back() {
      this.$router.back();
    },

    checkTerrainV3(check, terrain) {
      return check && check.terrain_type === terrain;
    },

    subscribeAA() {
      console.log('subscribe to AA');
    },

    onResize() {
      if (window.innerWidth < 601) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    },

    mulliganClass(item) {
      return item.mulligan ? 'mulligan pointer' : 'pointer';
    },

    clickedGolferRow(item) {
      this.$router.push({
        name: 'userProfile',
        path: `/users/${item.player_id}`,
        props: true,
        params: {
          id: `${item.player_id}`,
        },
      });
    },

    //* Not currently used. Leaving for future reference
    keyToFriendly(key) {
      if (key === undefined) return key;
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => {
          return str.toUpperCase();
        })
        .trim();
    },

    averageData(dataType, color, fnConvert) {
      let userShots = 0;
      let allShots = 0;
      let userTotal = 0;
      let allTotal = 0;
      let userAverage = 0;
      let allAverage = 0;
      const shotList = this?.shotData ?? [];

      if (shotList.length) {
        shotList.forEach((shot) => {
          const { clubTypeKey, mulligan, score, userID } = shot;
          //* Removing for testing -> score === 0 as practice rounds set score to 0
          // if (!shot[dataType] || clubTypeKey === 'Putter' || mulligan || score === 0) return;
          if (!shot[dataType] || clubTypeKey === 'Putter' || mulligan) return;
          if (this.filteredClubType !== 'All') {
            if (this.keyToFriendly(clubTypeKey) !== this.filteredClubType) return;
          }
          if (userID === this.$route.params.userID) {
            userShots++;
            userTotal += shot[dataType];
          }
          allShots++;
          allTotal += shot[dataType];
        });
      }

      userAverage = userTotal / userShots;
      allAverage = allTotal / allShots;
      if (typeof fnConvert === 'function') {
        userAverage = fnConvert(userAverage);
        allAverage = fnConvert(allAverage);
      }
      if (this.userShots.length === 0) {
        userAverage = 0;
        allAverage = 0;
      }

      return {
        labels: [`User Average`, `Round Average`],
        datasets: [
          {
            data: [Math.round(userAverage), Math.round(allAverage)],
            backgroundColor: [color, 'rgba(0, 0, 0, 0.2)'],
          },
        ],
      };
    },

    routeAnalytics() {
      this.$router.push({
        name: 'Analytics',
        props: true,
        params: {
          id: this.$route.params?.userID,
        },
      });
    },

    handleNewPortalNavigation() {
      const authUrl = buildPortalUrl('/auth/sync');
      const redirectPath = `/match/${this.$route.params.id}`;
      if (!authUrl || !redirectPath) {
        console.error('Missing redirect info — please log in again.');
        this.v3MatchRedirect = false;
        return;
      }

      const url = `${authUrl}&redirect=${encodeURIComponent(redirectPath)}`;
      setTimeout(() => {
        this.v3MatchRedirect = false;
        // Redirect to new portal app
        window.open(url, '_blank');
      }, 2000);
    },
  },
};
</script>

<style scoped>
/* helps with overlapping wording on table */
td,
tr {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.round-details {
  background: transparent !important;
  background-color: transparent !important;
}

.round-details-tabs .poll {
  white-space: normal;
  letter-spacing: normal;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1.5rem;
  align-items: center;
  text-align: center;
  height: 48px !important;
}

.progress {
  display: flex;
  align-items: center;
  justify-content: center;
}

.progressAA {
  position: absolute;
  z-index: 1;
  top: 25%;
  /* left: 40vw; */
  width: 100%;
}

.progress-main {
  margin-top: 100px;
}

.golfer,
tr.golfer > td {
  cursor: pointer;
}

.fillTD {
  display: block;
  padding: 10px 0;
}

.d-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, 60%);
  font-size: 20px;
}
#doughnut-chart,
.dg-chart {
  position: relative;
  z-index: 100;
}

.rel {
  position: relative;
}

td span {
  transition: all 150ms ease-in;
}

td span.active {
  font-weight: bold;
  font-size: 0.9rem;
  color: var(--primary, #ba2127);
}

.poll {
  margin-right: 12px;
  margin-top: 4px;
}

.theme--dark .poll {
  color: var(--white);
}

.filter-by {
  font-size: 16px;
  margin-top: 22px;
  /* margin-right: 12px; */
  /* width: 75px; */
}

.wrap {
  flex-wrap: wrap;
}

.v-input--selection-controls {
  margin-top: 0;
}

.shot-info-dialog {
  z-index: 99999 !important;
}

.analytics-card {
  border-top-right-radius: 0;
  border-top-left-radius: 0;
}

* >>> .v-tab:not(.v-tab--active) {
  color: rgba(255, 255, 255, 0.8) !important;
}

* >>> .v-card:not(.shot-card) {
  border: none;
}

* >>> .v-tabs__wrapper {
  padding-left: 20px;
}

* >>> .v-tabs-slider {
  height: 3px;
}

* >>> .v-card > :first-child:not(.v-btn):not(.v-chip) {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

* >>> .theme--light.v-data-table.shot-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.03);
}

* >>> .theme--dark.v-data-table.shot-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.5);
}

@media screen and (max-width: 1280px) {
  .d-title {
    top: 57%;
    font-size: 16px;
  }
}

@media screen and (max-width: 1024px) {
  .scorecard-container {
    display: flex;
    overflow: auto;
  }

  .d-title {
    top: 62%;
    font-size: 0.875rem;
  }
}

@media screen and (max-width: 960px) {
  .d-title {
    top: 50%;
    font-size: 1.375rem;
  }
}

@media screen and (min-width: 600px) {
  .scorecard-rotate {
    display: none;
  }
}

@media screen and (max-width: 600px) {
  * >>> .v-slide-group__prev {
    display: none !important;
  }

  * >>> .v-tabs__wrapper {
    padding-left: 0;
    overflow: auto;
  }
}

@media (max-width: 415px) {
}
</style>
